import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useCommissionerAccess } from '../hooks/useCommissionerAccess';
import { createLscLeague, LscApiError } from '../api';
import { GAME_KEY_OPTIONS, slugifyLeagueName } from '../constants';
import { lscPath, lscReturnTo } from '../routing';
import { parseLscMutationError } from '../entitlementBillingErrors';
import { COMMISSIONER_ACCESS_PAID_UNBOUND_GUIDANCE } from '../commissionerAccess';
import CommissionerUpgradePanel from '../components/CommissionerUpgradePanel';
import LoadingState from '../components/LoadingState';
import { useLscContentColumn } from '../lscContentColumn';

const LscCreateLeaguePage: React.FC = () => {
  useLscContentColumn('narrow');
  return (
    <LscAuthGate returnTo={lscReturnTo('/leagues/new')}>
      <CreateLeagueContent />
    </LscAuthGate>
  );
};

function CreateLeagueContent() {
  const { loading, error: accessError, presentation } = useCommissionerAccess();

  if (loading) {
    return <LoadingState message="Checking commissioner access..." />;
  }

  if (accessError && !presentation.showCreateForm) {
    return (
      <CommissionerUpgradePanel
        presentation={presentation}
        testId="create-league-access-error"
      />
    );
  }

  if (presentation.showUpgradePanel && !presentation.showCreateForm) {
    return <CommissionerUpgradePanel presentation={presentation} testId="create-league-gate" />;
  }

  return <CreateLeagueForm presentationState={presentation.state} />;
}

function CreateLeagueForm({
  presentationState,
}: {
  presentationState: string;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [gameKey, setGameKey] = useState('custom');
  const [visibility, setVisibility] = useState('members');
  const [loading, setLoading] = useState(false);
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const [blockedPresentation, setBlockedPresentation] = useState<ReturnType<
    typeof parseLscMutationError
  > | null>(null);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) setSlug(slugifyLeagueName(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSubmitBlocked(false);
    setBlockedPresentation(null);
    try {
      const league = await createLscLeague({
        name: name.trim(),
        slug: slug.trim(),
        game_key: gameKey,
        visibility,
      });
      navigate(lscPath(`/leagues/${league.id}/setup`));
    } catch (err) {
      if (err instanceof LscApiError && err.status === 403) {
        const guidance = parseLscMutationError(err, 'Could not create league.');
        setSubmitBlocked(true);
        setBlockedPresentation(guidance);
      } else {
        setSubmitBlocked(false);
        setBlockedPresentation(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitBlocked && blockedPresentation) {
    return (
      <div className="space-y-6 max-w-lg" data-testid="create-league-submit-blocked">
        <CommissionerUpgradePanel
          presentation={{
            state: 'free_member_blocked',
            canCreateLeague: false,
            headline: 'Start a league as commissioner',
            detailLines: [blockedPresentation.message],
            primaryCta: blockedPresentation.showPricingLink
              ? { code: 'pricing', label: 'View commissioner plans', href: '/pricing' }
              : blockedPresentation.showBillingLink
                ? { code: 'billing', label: 'Manage billing', href: '/billing' }
                : null,
            secondaryCtas: [
              { code: 'join', label: 'Enter invite code', href: '/join' },
              { code: 'leagues', label: 'Go to leagues', href: '/leagues' },
            ],
            showCreateForm: false,
            showUpgradePanel: true,
            showManageBilling: blockedPresentation.showBillingLink,
            showRenewBilling: false,
            showJoinPath: true,
            reasonCodes: ['submit_blocked'],
          }}
          testId="create-league-submit-upgrade"
        />
        <form className="card space-y-4" data-testid="create-league-preserved-form">
          <p className="text-sm text-secondary-400">Your league details are preserved below.</p>
          <div>
            <label className="block text-sm text-secondary-300 mb-1">League name</label>
            <input className="input-field" value={name} readOnly />
          </div>
          <div>
            <label className="block text-sm text-secondary-300 mb-1">URL slug</label>
            <input className="input-field" value={slug} readOnly />
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setSubmitBlocked(false);
              setBlockedPresentation(null);
            }}
          >
            Edit league details
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-lg">
      <h1 className="text-2xl font-bold text-white mb-6">Create league workspace</h1>
      {presentationState === 'commissioner_paid_unbound' ? (
        <div
          className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 mb-4 text-sm text-amber-100"
          data-testid="create-league-paid-unbound-guidance"
          role="status"
        >
          {COMMISSIONER_ACCESS_PAID_UNBOUND_GUIDANCE}
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="card space-y-4" data-testid="create-league-form">
        <div>
          <label className="block text-sm text-secondary-300 mb-1" htmlFor="league-name">
            League name
          </label>
          <input
            id="league-name"
            className="input-field"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm text-secondary-300 mb-1" htmlFor="league-slug">
            URL slug
          </label>
          <input
            id="league-slug"
            className="input-field"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            required
            pattern="[a-z0-9-]+"
          />
        </div>
        <div>
          <label className="block text-sm text-secondary-300 mb-1" htmlFor="game-key">
            Competition type
          </label>
          <select
            id="game-key"
            className="input-field"
            value={gameKey}
            onChange={(e) => setGameKey(e.target.value)}
          >
            {GAME_KEY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-secondary-300 mb-1" htmlFor="visibility">
            Visibility
          </label>
          <select
            id="visibility"
            className="input-field"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
          >
            <option value="members">Members only</option>
            <option value="private">Private</option>
            <option value="public">Public (future archive)</option>
          </select>
        </div>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create league'}
        </button>
      </form>
    </div>
  );
}

export default LscCreateLeaguePage;
